import React from 'react';
import { Field } from 'react-final-form';
import { Icon } from '@tt/ui';
import cx from 'classnames';
import { isDate } from 'moment';
import PropTypes from 'prop-types';

import { ReportedTypeEnum } from '../../../constants/enums/ReportedTypeEnum';
import {
  documentTypes,
  LISTING_DOCUMENT_TYPES,
} from '../../../helpers/listingDocuments';
import fieldRequired from '../../../validators/fieldRequired';
import Checkbox from '../../Checkbox';
import DateField from '../../DateField';
import IconButton from '../../IconButton';
import IconTooltip from '../../IconTooltip';
import Input from '../../Input';
import SelectField from '../../SelectField';
import { H5 } from '../../Typography';
import { useUploadDocumentContext } from '../context';

import styles from '../UploadDocumentModal.module.scss';

const DetailsStep = ({ values }) => {
  const { formData, onEdit } = useUploadDocumentContext();

  const isLease = formData.destination?.location_type === 'LEASE';

  const DocumentTypeOptions = [
    { label: '', value: '' },
    {
      label: 'Lease Agreement',
      value: ReportedTypeEnum.LEASE_AGREEMENT,
    },
    { label: 'Lease Addendum', value: ReportedTypeEnum.LEASE_ADDENDUM },
    {
      label: 'Condition Report',
      value: ReportedTypeEnum.CONDITION_REPORT,
    },
    { label: 'Forms', value: ReportedTypeEnum.FORMS },
    { label: 'Other', value: ReportedTypeEnum.OTHER },
  ];

  const ListingDocumentTypeOptions = [
    { label: '', value: '' },
    ...Object.keys(LISTING_DOCUMENT_TYPES).map((type) => ({
      value: type,
      label: documentTypes[type].category,
    })),
  ];

  return (
    <div className={styles.uploadedState}>
      <div className={styles.wrapper}>
        <H5>Destination folder</H5>
        <IconTooltip
          icon="info"
          size="md"
          className={styles.iconTooltip}
          tooltipClassName={styles.tooltip}
          tip={
            <ul className={styles.tooltipText}>
              <li>
                {' '}
                <b>Lease Documents:</b> Stores tenant-viewable documents for
                specific rental periods.
              </li>
              <li>
                {' '}
                <b>Property Documents:</b> Stored at the property or unit level
                and not visible to tenants.
              </li>
              <li>
                {' '}
                <b>‘My Documents’:</b> Your private documents not connected to a
                property or lease.
              </li>
            </ul>
          }
        />
      </div>

      <div className={styles.documentItem}>
        <div className={styles.fileName}>
          <Icon name="folder" color="close-lightbg" />
          <span className={styles.destinationText}>
            {formData.destination?.name}
          </span>
        </div>
        <IconButton
          icon="edit"
          iconProps={{ color: 'primary' }}
          className={styles.edit}
          onClick={() => onEdit()}
        />
      </div>

      <div className={styles.documentDetails}>
        <H5 className={cx(styles.generic, styles.spaced)}>Document details</H5>

        <Field
          name="reported_type"
          id="reported_type"
          component={SelectField}
          label="Category"
          validate={fieldRequired}
          options={isLease ? DocumentTypeOptions : ListingDocumentTypeOptions}
        />
        {values?.reported_type === 'OTHER' && (
          <Field
            id="type_other_detail"
            name="type_other_detail"
            component={Input}
            label="Other - Specify"
            hint="Optional"
          />
        )}
        {isLease && (
          <Field
            name="is_shared"
            component={Checkbox}
            labelClassName={styles.checkboxLabel}
            label={
              <>
                Share <span className={styles.hideMobile}>document</span> with
                all tenants on this lease
                <IconTooltip
                  icon="info"
                  size="md"
                  className={styles.iconTooltip}
                  tooltipClassName={styles.tooltip}
                  tip={
                    <span className={styles.tooltipText}>
                      Active tenants on this lease will be able to view this
                      document
                    </span>
                  }
                />
              </>
            }
          />
        )}
      </div>

      {!isLease && (
        <div className={styles.expirationDateContainer}>
          <Field
            className={styles.smallField}
            labelClassName={cx(styles.checkboxLabel, styles.hasTooltip)}
            label={
              <>
                Expiration date
                <IconTooltip
                  icon="info"
                  size="md"
                  className={cx(styles.iconTooltip, styles.floating)}
                  tooltipClassName={styles.tooltip}
                  tip={
                    <span className={styles.tooltipText}>
                      Add an expiration date to get a reminder 5 days before the
                      document or policy is about to expire.
                    </span>
                  }
                />
              </>
            }
            component={DateField}
            hint="(Optional)"
            name="expiration_date"
            id="expiration_date"
            autoComplete="off"
            inputProps={{
              autocomplete: false,
            }}
            validate={isDate}
          />
        </div>
      )}
    </div>
  );
};

DetailsStep.propTypes = {
  values: PropTypes.object,
};

export default DetailsStep;
